import React from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Star } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import SectionHeading from "@/components/marketing/SectionHeading";
import Reveal from "@/components/marketing/Reveal";

const TIERS = [
  {
    name: "Basic",
    price: 150,
    forYouIf: "You know your story and how to tell it. You just need the network.",
    whatWeDo: "You send us your content, and our team pitches it to the right journalists in your target segment.",
    specs: [
      "1 country",
      "1 sector",
      "50+ media outlets",
      "2-day turnaround",
      "2 images",
      "Basic analytics report",
    ],
    cta: "Choose Basic",
    popular: false,
  },
  {
    name: "Pro",
    price: 350,
    forYouIf: "You have a story worth telling, and you want expert hands on it.",
    whatWeDo: "A dedicated strategist sharpens your angle, curates the targets, and manages active outreach and follow-up.",
    specs: [
      "3 countries",
      "3 sectors",
      "200+ media outlets",
      "24-hour turnaround",
      "Content optimization",
      "5 images",
      "Full analytics report",
    ],
    cta: "Choose Pro",
    popular: true,
  },
  {
    name: "Enterprise",
    price: 750,
    forYouIf: "You have the news, but no time or team to run it.",
    whatWeDo: "Hand us your story. We write your content, draft the pitches, and run the full campaign end to end, like your own PR department.",
    specs: [
      "Unlimited countries",
      "Unlimited sectors (matched to relevance)",
      "500+ media outlets",
      "Same-day turnaround",
      "10 images",
      "2 translated versions",
      "Priority support",
      "Multimedia embeds",
      "Premium analytics",
    ],
    cta: "Choose Enterprise",
    popular: false,
  },
];

export default function OutreachTiers() {
  const { isAuthenticated } = useAuth();
  const ctaTo = isAuthenticated ? "/dashboard/new-order" : "/register";

  return (
    <section id="tiers" className="pt-5 pb-7 sm:pt-6 sm:pb-9 lg:pt-8 lg:pb-12 bg-canvas scroll-mt-20">
      <Container>
        <Reveal>
          <SectionHeading tag="OUTREACH TIERS" title="Find the level that fits you" subtitle="Wherever you are, from a team of your own to no time at all, there is a tier built around how much you need us." />
        </Reveal>
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {TIERS.map((tier, index) => (
            <Reveal variant="card" delay={index * 0.08} key={tier.name}>
              <div
                className={`relative rounded-xl border p-7 lg:p-8 flex flex-col ${
                  tier.popular
                    ? "border-cobalt/40 bg-cobalt/[0.03] shadow-md"
                    : "border-border bg-white"
                }`}
              >
              {tier.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-cobalt text-white text-xs font-medium px-3 py-1 rounded-full shadow-sm whitespace-nowrap">
                  <Star className="w-3 h-3" /> Most Popular
                </span>
              )}
              <h3 className="font-heading text-xl font-bold text-bone mb-1">{tier.name}</h3>
              <div className="mb-5">
                <span className="font-heading text-4xl font-bold text-bone">${tier.price}</span>
              </div>
              <div className="mb-5">
                <span className="block font-mono text-xs uppercase tracking-widest text-cobalt/60 mb-2">For you if</span>
                <p className="text-sm text-bone font-semibold leading-relaxed">{tier.forYouIf}</p>
              </div>
              <div className="bg-cobalt/5 rounded-lg p-4 mb-5">
                <span className="block font-mono text-xs uppercase tracking-widest text-cobalt/60 mb-2">What we do</span>
                <p className="text-sm text-bone leading-relaxed">{tier.whatWeDo}</p>
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {tier.specs.map((spec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-steel">
                    <Check className="w-4 h-4 text-cobalt shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{spec}</span>
                  </li>
                ))}
              </ul>
              <Button asChild variant={tier.popular ? "default" : "outline"} className="w-full">
                <Link to={ctaTo}>{tier.cta}</Link>
              </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}